import { useState } from 'react';
import { formatDate } from '@brimo/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Dialog, Skeleton, Textarea, TextField, useToast } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import {
  billingKeys,
  submitPaymentMethod,
  useRequestCompanyBilling,
  useSaveBillingAddress,
  useUserBilling,
  type OwnBilling,
} from './api';
import { ActionReason, BillingModeCard, InvoiceList, PaymentMethodForm, PlanCard } from './ui';
import styles from './BillingPage.module.css';

/**
 * „Abrechnung" (Einstellungen 18/19, M9): company mode shows the info card of 18; own billing (switched on by
 * an admin in Einstellungen 27) shows the tariff, the invoices, the payment method and the billing address of
 * 19. Every payment-provider action is disabled with the server's reason (D-07).
 */
export function BillingPage() {
  const mobile = useIsMobile();
  const billing = useUserBilling();
  const data = billing.data;

  if (mobile) {
    return (
      <div className={styles.mobile}>
        {billing.isError ? <Banner tone="danger">{errorMessage(billing.error)}</Banner> : null}
        {billing.isPending ? <BillingSkeleton /> : null}
        {data?.mode === 'company' ? <BillingModeCard billing={data} /> : null}
        {data?.mode === 'own' ? <OwnBillingMobile billing={data} /> : null}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.eyebrow}>Meine Einstellungen</span>
          <h2 className={styles.title}>Abrechnung</h2>
          {data?.mode === 'own' ? <p className={styles.description}>{data.title}</p> : null}
        </div>
        {data?.mode === 'own' ? <span className={styles.ownPill}>Eigene Abrechnung</span> : null}
      </div>
      {billing.isError ? <Banner tone="danger">{errorMessage(billing.error)}</Banner> : null}
      {billing.isPending ? <BillingSkeleton /> : null}
      {data?.mode === 'company' ? <BillingModeCard billing={data} /> : null}
      {data?.mode === 'own' ? <OwnBillingDesktop billing={data} /> : null}
    </div>
  );
}

function BillingSkeleton() {
  return (
    <div className={styles.card} style={{ maxWidth: 760 }}>
      <Skeleton width={44} height={44} />
      <Skeleton width="60%" height={20} />
      <Skeleton width="80%" />
      <Skeleton variant="block" height={60} />
    </div>
  );
}

function OverdueBanner({ billing }: { billing: OwnBilling }) {
  const { toast } = useToast();
  const request = useRequestCompanyBilling();
  if (!billing.overdueNotice) return null;
  const action = billing.actions.requestCompanyBilling;
  return (
    <Banner
      tone="danger"
      title={billing.overdueNotice.title}
      actions={
        action.allowed ? (
          <Button
            variant="secondary"
            size="small"
            loading={request.isPending}
            onClick={() =>
              request.mutate(undefined, {
                onSuccess: () => toast({ message: 'Ihre Anfrage liegt bei Ihrer Administration.' }),
              })
            }
          >
            Über Firma abrechnen lassen
          </Button>
        ) : null
      }
    >
      {billing.overdueNotice.text}
      {!action.allowed && action.reason ? ` ${action.reason}` : null}
      {request.isError ? ` ${errorMessage(request.error)}` : null}
    </Banner>
  );
}

function PaymentMethodPanel({ billing, compact }: { billing: OwnBilling; compact?: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const add = billing.actions.addPaymentMethod;
  return (
    <>
      <div className={compact ? styles.mobileMethod : styles.method}>
        <span className={styles.methodIcon} aria-hidden="true" />
        <div className={styles.methodText}>
          <span className={styles.methodTitle}>Noch kein Zahlungsmittel hinterlegt</span>
          <span className={styles.methodSub}>
            {billing.deadlineOn
              ? `Frist ${formatDate(billing.deadlineOn)} · bis dahin läuft der Platz über die Firmenrechnung`
              : 'Der Platz läuft über die Firmenrechnung.'}
          </span>
        </div>
      </div>
      <button
        type="button"
        className={compact ? `${styles.addButton} ${styles.mobileButton}` : styles.addButton}
        disabled={!add.allowed}
        aria-describedby={add.allowed ? undefined : 'billing-add-reason'}
        onClick={() => setOpen(true)}
      >
        + Zahlungsmittel hinzufügen
      </button>
      <ActionReason actions={[add]} id="billing-add-reason" />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Zahlungsmittel"
        title="Zahlungsmittel hinzufügen"
        width={620}
      >
        <PaymentMethodForm
          bare
          header={
            <PlanCard
              variant="compact"
              name={billing.plan.name}
              detail={billing.plan.note}
              priceLabel={billing.plan.priceNetLabel}
            />
          }
          defaultAddress={billing.billingAddress?.replace(/\n/g, ', ') ?? ''}
          defaultVatId={billing.vatId ?? ''}
          secondary={
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
          }
          onSubmit={async (body) => {
            await submitPaymentMethod(body);
            setOpen(false);
            toast({ message: 'Zahlungsmittel gespeichert.' });
            await queryClient.invalidateQueries({ queryKey: billingKeys.root });
          }}
        />
      </Dialog>
    </>
  );
}

function AddressCard({ billing }: { billing: OwnBilling }) {
  const { toast } = useToast();
  const save = useSaveBillingAddress();
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState('');
  const [vatId, setVatId] = useState('');
  const fields = save.error instanceof ApiError ? save.error.fields : {};
  const change = billing.actions.changeAddress;

  function open() {
    setAddress(billing.billingAddress ?? '');
    setVatId(billing.vatId ?? '');
    save.reset();
    setEditing(true);
  }

  return (
    <section className={`${styles.sideCard} ${styles.sideCardCompact}`} aria-label="Rechnungsanschrift">
      <h3 className={styles.sideTitle}>Rechnungsanschrift</h3>
      {editing ? (
        <form
          className={styles.addressForm}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(
              { address: address.trim(), vatId: vatId.trim() === '' ? null : vatId.trim() },
              {
                onSuccess: () => {
                  setEditing(false);
                  toast({ message: 'Rechnungsanschrift gespeichert.' });
                },
              },
            );
          }}
        >
          <Textarea
            label="Anschrift"
            rows={3}
            value={address}
            error={fields['address']}
            onChange={(event) => setAddress(event.target.value)}
          />
          <TextField
            label="Umsatzsteuer-ID"
            optional
            value={vatId}
            error={fields['vatId']}
            onChange={(event) => setVatId(event.target.value)}
          />
          {save.isError && Object.keys(fields).length === 0 ? (
            <Banner tone="danger">{errorMessage(save.error)}</Banner>
          ) : null}
          <div className={styles.addressActions}>
            <Button type="submit" size="small" loading={save.isPending}>
              Speichern
            </Button>
            <Button type="button" variant="secondary" size="small" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      ) : (
        <>
          {billing.billingAddress ? (
            <span className={styles.address}>
              {billing.billingAddress}
              {billing.vatId ? `\n${billing.vatId}` : ''}
            </span>
          ) : (
            <span className={styles.addressEmpty}>Noch keine Rechnungsanschrift hinterlegt.</span>
          )}
          <Button
            variant="secondary"
            size="small"
            className={styles.smallButton}
            disabled={!change.allowed}
            onClick={open}
          >
            Ändern
          </Button>
          <ActionReason actions={[change]} />
        </>
      )}
    </section>
  );
}

function OwnBillingDesktop({ billing }: { billing: OwnBilling }) {
  const { changePlan, cancel } = billing.actions;
  return (
    <>
      <OverdueBanner billing={billing} />
      <div className={styles.grid}>
        <div className={styles.column}>
          <section className={styles.card} aria-label="Tarif">
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>Tarif</h3>
              <span className={styles.cardMeta}>
                nächste Abbuchung {formatDate(billing.plan.firstDebitOn)}
              </span>
            </div>
            <PlanCard
              name={billing.plan.name}
              detail={billing.plan.features ?? billing.plan.note}
              priceLabel={billing.plan.priceLabel}
            />
            <div className={styles.buttons}>
              <Button variant="secondary" size="small" disabled={!changePlan.allowed}>
                Tarif wechseln
              </Button>
              <Button variant="ghost" size="small" disabled={!cancel.allowed}>
                Kündigen
              </Button>
            </div>
            <ActionReason actions={[changePlan, cancel]} />
          </section>
          <InvoiceList invoices={billing.invoices} />
        </div>
        <div className={styles.column}>
          <section className={styles.sideCard} aria-label="Zahlungsmittel">
            <h3 className={styles.sideTitle}>Zahlungsmittel</h3>
            <PaymentMethodPanel billing={billing} />
          </section>
          <AddressCard billing={billing} />
        </div>
      </div>
    </>
  );
}

function OwnBillingMobile({ billing }: { billing: OwnBilling }) {
  return (
    <>
      <OverdueBanner billing={billing} />
      <PlanCard
        variant="mobile"
        name={billing.plan.name}
        priceLabel={billing.plan.priceNetLabel}
        suffix={`nächste Abbuchung ${formatDate(billing.plan.firstDebitOn)}`}
      />
      <section className={styles.mobileCard} aria-label="Zahlungsmittel">
        <span className={styles.mobileCardTitle}>Zahlungsmittel</span>
        <PaymentMethodPanel billing={billing} compact />
      </section>
      <InvoiceList invoices={billing.invoices} variant="mobile" />
      <div className={styles.notice}>{billing.notice}</div>
    </>
  );
}
